import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Clock, Brain, Zap, Check, X, BookOpen, Shield, Menu, Star, Gift, MapPin, BarChart3, FileCheck, Smartphone, ListChecks, Target, ArrowRight, Calendar, Trophy, FileText } from 'lucide-react';
// Meta Pixel ID
const META_PIXEL_ID = '1240798207991456';
// Initialize Meta Pixel - matches Facebook's official base code exactly
const initMetaPixel = () => {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w.fbq && w.fbq.loaded) return;
  const n: any = w.fbq = function () {
    if (n.callMethod) {
      n.callMethod.apply(n, arguments);
    } else {
      n.queue.push(arguments);
    }
  };
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
  w.fbq('init', META_PIXEL_ID);
  w.fbq('track', 'PageView');
  const noscriptImg = document.createElement('img');
  noscriptImg.height = 1;
  noscriptImg.width = 1;
  noscriptImg.style.display = 'none';
  noscriptImg.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
  document.body.appendChild(noscriptImg);
};
// Track AddToCart event (2 carts as requested)
const trackAddToCart = () => {
  const w = window as any;
  if (typeof w.fbq === 'function') {
    w.fbq('track', 'AddToCart', {
      content_name: 'El Reset de 14 Días',
      content_ids: ['48274359353594'],
      content_type: 'product',
      value: 6.39,
      currency: 'USD',
      num_items: 2
    });
  }
};
// --- Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} className={className} data-id="element-2">
      {children}
    </motion.div>;
};
const Button = ({
  children,
  variant = 'primary',
  className = '',
  href,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary';
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900';
  const variants = {
    primary: 'bg-[#00D9C0] text-black hover:bg-[#00c4ad] hover:shadow-[0_0_20px_rgba(0,217,192,0.4)] focus:ring-[#00D9C0]',
    secondary: 'bg-zinc-800 text-white border border-zinc-700 hover:bg-zinc-700 focus:ring-zinc-600'
  };
  if (href) {
    return <a href={href} className={`${baseStyles} ${variants[variant]} ${className}`} target="_blank" rel="noopener noreferrer" onClick={onClick as (e: React.MouseEvent<HTMLAnchorElement>) => void} data-id="element-3">
        {children}
      </a>;
  }
  return <button className={`${baseStyles} ${variants[variant]} ${className}`} onClick={onClick as (e: React.MouseEvent<HTMLButtonElement>) => void} {...props} data-id="element-4">
      {children}
    </button>;
};
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-20 md:py-32 px-6 ${className}`} data-id="element-5">
    <div className="max-w-4xl mx-auto" data-id="element-6">{children}</div>
  </section>;
// --- Modal Component ---
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  return <AnimatePresence data-id="element-7">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]" data-id="element-8" />
          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl md:w-full md:max-h-[80vh] bg-zinc-900 border border-zinc-800 rounded-2xl z-[101] flex flex-col overflow-hidden" data-id="element-9">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-zinc-800" data-id="element-10">
              <h2 className="text-xl font-bold text-white" data-id="element-11">{title}</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors" data-id="element-12">
                <X className="w-4 h-4" data-id="element-13" />
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 text-zinc-300 text-sm leading-relaxed space-y-4" data-id="element-14">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
// --- Main Page ---
export function SalesPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // Initialize Meta Pixel and fire PageView on mount
  useEffect(() => {
    initMetaPixel();
  }, []);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Handle purchase button click — fire AddToCart with 2 carts, then navigate
  const handlePurchaseClick = () => {
    trackAddToCart();
  };
  return <div className="min-h-screen bg-[#0a0a0a] text-zinc-100 font-sans selection:bg-[#00D9C0] selection:text-black overflow-x-hidden" data-id="element-15">
      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-16">
        <p className="text-zinc-400 text-xs mb-4" data-id="element-17">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-18">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-19">
          Recopilamos información que nos proporcionas directamente al realizar
          una compra, incluyendo tu nombre, dirección de correo electrónico e
          información de pago. No almacenamos información de tarjetas de
          crédito; todos los pagos son procesados de forma segura por nuestros
          proveedores de pago.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-20">
          2. Uso de la Información
        </h3>
        <p data-id="element-21">Utilizamos la información recopilada para:</p>
        <ul className="list-disc list-inside ml-4 space-y-1 mt-2" data-id="element-22">
          <li data-id="element-23">Procesar y entregar tu compra</li>
          <li data-id="element-24">Enviarte actualizaciones sobre tu pedido</li>
          <li data-id="element-25">Responder a tus preguntas y solicitudes</li>
          <li data-id="element-26">
            Enviarte comunicaciones de marketing (solo si has dado tu
            consentimiento)
          </li>
        </ul>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-27">
          3. Protección de Datos
        </h3>
        <p data-id="element-28">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o alteración.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-29">
          4. Tus Derechos
        </h3>
        <p data-id="element-30">
          Tienes derecho a acceder, corregir o eliminar tu información personal.
          También puedes oponerte al procesamiento de tus datos o solicitar la
          portabilidad de los mismos.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-31">
          5. Cookies
        </h3>
        <p data-id="element-32">
          Utilizamos cookies esenciales para el funcionamiento del sitio y
          cookies analíticas para mejorar tu experiencia. Puedes gestionar tus
          preferencias de cookies en la configuración de tu navegador.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-33">
          6. Cambios a esta Política
        </h3>
        <p data-id="element-34">
          Podemos actualizar esta política ocasionalmente. Te notificaremos
          sobre cambios significativos publicando la nueva política en esta
          página.
        </p>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-35">
        <p className="text-zinc-400 text-xs mb-4" data-id="element-36">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-37">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-38">
          Al acceder y utilizar este sitio web y realizar una compra, aceptas
          estar sujeto a estos términos y condiciones de uso.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-39">
          2. Producto Digital
        </h3>
        <p data-id="element-40">
          "El Reset de 14 Días" es un producto digital (ebook) que se entrega
          electrónicamente. Una vez completada la compra, recibirás acceso
          inmediato al contenido.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-41">
          3. Uso Personal
        </h3>
        <p data-id="element-42">
          El contenido adquirido es para tu uso personal únicamente. No está
          permitido:
        </p>
        <ul className="list-disc list-inside ml-4 space-y-1 mt-2" data-id="element-43">
          <li data-id="element-44">Redistribuir, revender o compartir el contenido</li>
          <li data-id="element-45">Modificar o crear obras derivadas</li>
          <li data-id="element-46">Utilizar el contenido con fines comerciales sin autorización</li>
        </ul>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-47">
          4. Política de Reembolso
        </h3>
        <p data-id="element-48">
          Debido a la naturaleza digital del producto, todas las ventas son
          finales. Sin embargo, si experimentas problemas técnicos para acceder
          al contenido, trabajaremos contigo para resolverlos.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-49">
          5. Descargo de Responsabilidad
        </h3>
        <p data-id="element-50">
          El contenido de este ebook es solo para fines informativos y
          educativos. No sustituye el consejo médico, psicológico o profesional.
          Consulta a un profesional de la salud antes de comenzar cualquier
          programa de ejercicio o bienestar.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-51">
          6. Limitación de Responsabilidad
        </h3>
        <p data-id="element-52">
          No garantizamos resultados específicos. Los resultados pueden variar
          según el individuo y su compromiso con el programa.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-53">
          7. Propiedad Intelectual
        </h3>
        <p data-id="element-54">
          Todo el contenido, incluyendo textos, gráficos y diseño, está
          protegido por derechos de autor y es propiedad exclusiva de El Reset
          de 14 Días.
        </p>

        <h3 className="text-white font-semibold text-base mt-6 mb-2" data-id="element-55">
          8. Modificaciones
        </h3>
        <p data-id="element-56">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Los cambios entrarán en vigor inmediatamente después de su
          publicación.
        </p>
      </Modal>

      {/* Sticky Mobile CTA */}
      <motion.div initial={{
      y: 100
    }} animate={{
      y: 0
    }} transition={{
      delay: 2
    }} className="fixed bottom-0 left-0 right-0 p-4 bg-[#0a0a0a]/90 backdrop-blur-md border-t border-zinc-800 z-50 md:hidden" data-id="element-57">
        <Button onClick={scrollToPricing} className="w-full py-3 text-base" data-id="element-58">
          Quiero el Reset →{' '}
          <span className="line-through text-zinc-600 mr-1" data-id="element-59">$12.99</span> $6.39
        </Button>
      </motion.div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-40 bg-[#0a0a0a]/80 backdrop-blur-sm border-b border-zinc-900" data-id="element-60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-61">
          <span className="font-bold text-xl tracking-tight text-white" data-id="element-62">
            El Reset<span className="text-[#00D9C0]" data-id="element-63">.</span>
          </span>
          <button onClick={scrollToPricing} className="hidden md:block text-sm font-medium text-zinc-400 hover:text-[#00D9C0] transition-colors" data-id="element-64">
            Obtener acceso
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 overflow-hidden" data-id="element-65">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[#00D9C0] opacity-[0.03] blur-[120px] rounded-full pointer-events-none" data-id="element-66" />

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-67">
          <div className="space-y-8" data-id="element-68">
            <FadeIn data-id="element-69">
              <div className="inline-block px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-[#00D9C0] text-sm font-medium mb-4" data-id="element-70">
                Para personas con ansiedad y estrés alto
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight text-white" data-id="element-71">
                No te falta disciplina. <br data-id="element-72" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00D9C0] to-teal-600" data-id="element-73">
                  Te falta un reset.
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-74">
              <p className="text-xl md:text-2xl text-zinc-400 font-light leading-relaxed" data-id="element-75">
                10 minutos al día para dejar de volver a cero.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-76">
              <div className="space-y-6 text-lg text-zinc-300 border-l-2 border-zinc-800 pl-6 my-8" data-id="element-77">
                <p data-id="element-78">No es que seas flojo/a.</p>
                <p data-id="element-79">
                  Es que estás cansado/a… y cuando estás cansado/a, tu mente
                  empieza a negociar.
                </p>
                <p className="italic text-zinc-500" data-id="element-80">
                  "Hoy no." "Mañana con calma." "Si no puedo hacerlo bien, mejor
                  no hago nada."
                </p>
                <p data-id="element-81">
                  Y de repente estás otra vez en el punto donde te juras que el
                  lunes sí.
                </p>
                <p className="font-medium text-white" data-id="element-82">
                  Este no es otro plan "motivacional". Es un sistema pequeño y
                  repetible para los días en que tu ansiedad manda.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-83">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-84">
                <Button onClick={scrollToPricing} data-id="element-85">
                  Quiero el Reset →{' '}
                  <span className="line-through text-zinc-600 mr-1" data-id="element-86">
                    $12.99
                  </span>{' '}
                  $6.39
                </Button>
                <div className="flex items-center gap-2 text-sm text-zinc-500 px-4 py-2" data-id="element-87">
                  <Shield className="w-4 h-4" data-id="element-88" />
                  <span data-id="element-89">Pago único y seguro</span>
                </div>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.4} className="relative hidden md:block" data-id="element-90">
            <motion.div animate={{
            y: [0, -20, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative z-10" data-id="element-91">
              <img src="https://cdn.magicpatterns.com/uploads/ovMysJcQmeAgzfRZRPLDCp/El_reset_de_14.png" alt="El Reset de 14 Días Ebook Cover" className="w-full max-w-md mx-auto rounded-lg shadow-2xl shadow-[#00D9C0]/10 rotate-[-3deg] hover:rotate-0 transition-transform duration-500" data-id="element-92" />
            </motion.div>
            {/* Decorative elements behind image */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm h-[400px] bg-zinc-800/50 rounded-2xl -rotate-6 -z-10 border border-zinc-700/50" data-id="element-93" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm h-[400px] bg-zinc-900/50 rounded-2xl rotate-3 -z-20 border border-zinc-800/50" data-id="element-94" />
          </FadeIn>
        </div>
      </section>

      {/* Pain Section */}
      <Section className="bg-zinc-900/50 border-y border-zinc-900" data-id="element-95">
        <FadeIn data-id="element-96">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-97">
            Si tu historia es: <br data-id="element-98" />
            <span className="text-zinc-500" data-id="element-99">"Empiezo fuerte… y me caigo"</span>
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-100">
          <FadeIn className="space-y-6 text-lg text-zinc-300" data-id="element-101">
            <p data-id="element-102">Déjame adivinar cómo se siente.</p>
            <p data-id="element-103">
              Tú sí sabes qué hacer. Tú sí has visto rutinas. Tú sí te has
              inscrito al gym, has guardado videos, has comprado cositas.
            </p>
            <p className="font-medium text-white" data-id="element-104">
              El problema no es información.
            </p>
            <p data-id="element-105">
              El problema es el momento exacto en que el trabajo te exprime, la
              vida se complica, la ansiedad sube y tu mente te suelta la frase
              que te mata la semana:
            </p>
            <p className="text-2xl font-bold text-[#00D9C0]" data-id="element-106">
              "Ya no importa."
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-zinc-950 p-8 rounded-2xl border border-zinc-800" data-id="element-107">
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-6" data-id="element-108">
              El Ciclo del Abandono
            </h3>
            <div className="space-y-4" data-id="element-109">
              {['Fallas un día', 'Llega la culpa', 'Te castigas', 'Abandonas', 'Vuelves a cero', 'El lunes te exiges el doble'].map((step, i) => <div key={i} className="flex items-center gap-4" data-id="element-110">
                  <div className="w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-[#00D9C0] font-bold text-sm shrink-0" data-id="element-111">
                    {i + 1}
                  </div>
                  <span className="text-zinc-300" data-id="element-112">{step}</span>
                </div>)}
            </div>
            <p className="mt-8 text-sm text-zinc-500 italic border-t border-zinc-900 pt-4" data-id="element-113">
              Y cada reinicio duele más porque ya no solo estás peleando con el
              gym… Estás peleando con tu confianza.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Why Fails Section */}
      <Section data-id="element-114">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-115">
          <FadeIn data-id="element-116">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-117">
              Por qué "la disciplina" no te está funcionando
            </h2>
            <p className="text-xl text-zinc-400" data-id="element-118">
              La mayoría de planes están diseñados para tu mejor versión.
            </p>
          </FadeIn>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-119">
          <FadeIn className="bg-zinc-900 p-8 rounded-2xl border border-zinc-800 opacity-50" data-id="element-120">
            <div className="flex items-center gap-3 mb-6" data-id="element-121">
              <X className="text-red-500" data-id="element-122" />
              <h3 className="text-xl font-bold" data-id="element-123">Tu "Mejor Versión"</h3>
            </div>
            <ul className="space-y-3 text-zinc-400" data-id="element-124">
              <li data-id="element-125">• Cuando dormiste bien</li>
              <li data-id="element-126">• Cuando estás tranquilo/a</li>
              <li data-id="element-127">• Cuando tienes tiempo</li>
              <li data-id="element-128">• Cuando no estás ansioso/a</li>
            </ul>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-gradient-to-b from-zinc-900 to-zinc-950 p-8 rounded-2xl border border-[#00D9C0]/30 shadow-[0_0_30px_rgba(0,217,192,0.05)] relative overflow-hidden" data-id="element-129">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#00D9C0]" data-id="element-130" />
            <div className="flex items-center gap-3 mb-6" data-id="element-131">
              <Check className="text-[#00D9C0]" data-id="element-132" />
              <h3 className="text-xl font-bold text-white" data-id="element-133">Tus Días Reales</h3>
            </div>
            <p className="text-zinc-300 mb-4" data-id="element-134">
              Tú no compras un plan para tus días perfectos. Tú lo necesitas
              para tus días reales.
            </p>
            <p className="text-zinc-300" data-id="element-135">
              En tus días reales, tu sistema nervioso busca alivio rápido. Y el
              esfuerzo se siente como amenaza. Así que tu mente hace lo que
              sabe: <span className="text-[#00D9C0] font-bold" data-id="element-136">evita</span>.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Solution Section */}
      <Section className="bg-zinc-900/30 border-y border-zinc-900" data-id="element-137">
        <div className="text-center mb-16" data-id="element-138">
          <FadeIn data-id="element-139">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-140">
              La solución no es "más ganas". <br data-id="element-141" />
              Es un protocolo.
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto" data-id="element-142">
              Presentando el{' '}
              <span className="text-[#00D9C0] font-bold" data-id="element-143">
                Micro-Reset Diario (10 min)
              </span>
              . Un sistema diseñado para cuando no tienes motivación.
            </p>
          </FadeIn>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-144">
          {[{
          icon: Brain,
          title: '1. Guion mental',
          time: '2 min',
          desc: 'Corta la culpa y la negociación interna antes de que te paralicen.'
        }, {
          icon: Zap,
          title: '2. Acción mínima',
          time: '6 min',
          desc: 'Movimiento simple, cero debate. Engañamos a la resistencia.'
        }, {
          icon: Shield,
          title: '3. Cierre de ansiedad',
          time: '2 min',
          desc: 'Sales con victoria, no con deuda. Reconstruyes confianza.'
        }].map((item, i) => <FadeIn key={i} delay={i * 0.1} className="bg-zinc-950 p-8 rounded-2xl border border-zinc-800 hover:border-[#00D9C0]/50 transition-colors group" data-id="element-145">
              <div className="w-12 h-12 rounded-full bg-zinc-900 flex items-center justify-center mb-6 text-[#00D9C0] group-hover:scale-110 transition-transform" data-id="element-146">
                <item.icon className="w-6 h-6" data-id="element-147" />
              </div>
              <div className="flex justify-between items-baseline mb-2" data-id="element-148">
                <h3 className="text-xl font-bold text-white" data-id="element-149">{item.title}</h3>
                <span className="text-xs font-mono text-zinc-500 border border-zinc-800 px-2 py-1 rounded" data-id="element-150">
                  {item.time}
                </span>
              </div>
              <p className="text-zinc-400 leading-relaxed" data-id="element-151">{item.desc}</p>
            </FadeIn>)}
        </div>

        <FadeIn delay={0.4} className="mt-12 text-center" data-id="element-152">
          <p className="text-lg text-zinc-300 mb-8" data-id="element-153">
            No es "hacer poco". Es construir evidencia diaria de que tú{' '}
            <span className="font-bold text-white" data-id="element-154">vuelves</span>.
          </p>
          <Button onClick={scrollToPricing} data-id="element-155">
            Empezar el Reset →{' '}
            <span className="line-through text-zinc-600 mr-1" data-id="element-156">$12.99</span>{' '}
            $6.39
          </Button>
        </FadeIn>
      </Section>

      {/* What Changes Section */}
      <Section data-id="element-157">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-158">
          <FadeIn data-id="element-159">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-160">
              Lo que cambia en 14 días
            </h2>
            <p className="text-zinc-400 mb-8" data-id="element-161">
              No te voy a vender fantasías. En 14 días, lo que cambia es esto:
            </p>
            <ul className="space-y-4" data-id="element-162">
              {['Vuelves más rápido cuando fallas', 'Discutes menos con tu cabeza', 'Te culpas menos', 'Haces lo mínimo sin drama', 'Recuperas una sensación que vale oro: CONTROL'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-163">
                  <Star className="w-5 h-5 text-[#00D9C0] shrink-0 fill-[#00D9C0]/20 mt-1" data-id="element-164" />
                  <span className="text-zinc-200 text-lg" data-id="element-165">{item}</span>
                </li>)}
            </ul>
            <p className="mt-8 text-zinc-500 text-sm" data-id="element-166">
              Y cuando esa base existe, ya puedes subir intensidad sin romperte.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="relative" data-id="element-167">
            <div className="absolute inset-0 bg-gradient-to-tr from-[#00D9C0]/20 to-transparent rounded-2xl blur-2xl" data-id="element-168" />
            <div className="relative bg-zinc-900 border border-zinc-800 p-8 rounded-2xl" data-id="element-169">
              <div className="flex items-center gap-4 mb-6" data-id="element-170">
                <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center" data-id="element-171">
                  <Clock className="w-6 h-6 text-[#00D9C0]" data-id="element-172" />
                </div>
                <div data-id="element-173">
                  <div className="text-sm text-zinc-500" data-id="element-174">Tiempo diario</div>
                  <div className="text-xl font-bold text-white" data-id="element-175">10 Minutos</div>
                </div>
              </div>
              <div className="space-y-4" data-id="element-176">
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden" data-id="element-177">
                  <motion.div initial={{
                  width: 0
                }} whileInView={{
                  width: '100%'
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut'
                }} className="h-full bg-[#00D9C0]" data-id="element-178" />
                </div>
                <div className="flex justify-between text-sm text-zinc-400" data-id="element-179">
                  <span data-id="element-180">Día 1</span>
                  <span data-id="element-181">Día 14</span>
                </div>
                <p className="text-center text-white font-medium pt-4" data-id="element-182">
                  Consistencia {'>'} Intensidad
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* For Who / Not For Who */}
      <Section className="bg-zinc-950" data-id="element-183">
        <div className="grid md:grid-cols-2 gap-8" data-id="element-184">
          <FadeIn className="bg-zinc-900/30 p-8 rounded-2xl border border-zinc-800" data-id="element-185">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-186">
              <Check className="text-[#00D9C0]" data-id="element-187" /> Esto es para ti si:
            </h3>
            <ul className="space-y-3" data-id="element-188">
              {['Estás en USA, con estrés real, vida real', "Te pasa '2 semanas sí, 2 no'", 'Te gana la ansiedad, la culpa o el cansancio', 'Quieres un sistema simple que no dependa de motivación'].map((item, i) => <li key={i} className="flex items-start gap-3 text-zinc-300" data-id="element-189">
                  <Check className="w-5 h-5 text-[#00D9C0] shrink-0 mt-0.5" data-id="element-190" />
                  <span data-id="element-191">{item}</span>
                </li>)}
            </ul>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-zinc-900/30 p-8 rounded-2xl border border-zinc-800" data-id="element-192">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-193">
              <X className="text-zinc-500" data-id="element-194" /> Esto NO es para ti si:
            </h3>
            <ul className="space-y-3" data-id="element-195">
              {['Buscas un plan avanzado de hipertrofia', 'Quieres macros perfectas y detalles técnicos', 'Quieres una transformación extrema en 30 días'].map((item, i) => <li key={i} className="flex items-start gap-3 text-zinc-400" data-id="element-196">
                  <X className="w-5 h-5 text-zinc-600 shrink-0 mt-0.5" data-id="element-197" />
                  <span data-id="element-198">{item}</span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
        <FadeIn className="text-center mt-8" data-id="element-199">
          <p className="text-zinc-400" data-id="element-200">
            Aquí ganamos por lo que la mayoría desprecia:{' '}
            <span className="text-white font-bold" data-id="element-201">lo mínimo sostenible.</span>
          </p>
        </FadeIn>
      </Section>

      {/* What's Included */}
      <Section data-id="element-202">
        <FadeIn data-id="element-203">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-204">
            Qué incluye exactamente
          </h2>
        </FadeIn>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-205">
          {[{
          icon: BookOpen,
          text: 'Ebook El Reset de 14 Días (Ansiedad → Disciplina)'
        }, {
          icon: Zap,
          text: 'El sistema Micro-Reset Diario (10 min) explicado paso a paso'
        }, {
          icon: Menu,
          text: "'Puertas 1–3': qué hacer según tu nivel de energía/ansiedad"
        }, {
          icon: Clock,
          text: 'Plan de 14 días simple'
        }, {
          icon: Shield,
          text: 'Anti-caídas: cómo retomar sin reiniciar desde cero'
        }, {
          icon: Star,
          text: 'FAQ, glosario, recursos extra'
        }].map((item, i) => <FadeIn key={i} delay={i * 0.05} className="flex items-start gap-4 p-6 bg-zinc-900 rounded-xl border border-zinc-800" data-id="element-206">
              <item.icon className="w-6 h-6 text-[#00D9C0] shrink-0" data-id="element-207" />
              <span className="text-zinc-300 font-medium" data-id="element-208">{item.text}</span>
            </FadeIn>)}
        </div>
      </Section>

      {/* Bonos Gratis Section */}
      <Section className="bg-zinc-950 border-y border-zinc-900 relative overflow-hidden" data-id="element-209">
        {/* Decorative background element */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#00D9C0] opacity-[0.03] blur-[100px] rounded-full pointer-events-none" data-id="element-210" />

        {/* 1. Bonos Header */}
        <div className="max-w-3xl mx-auto text-center mb-20" data-id="element-211">
          <FadeIn data-id="element-212">
            <div className="inline-block px-4 py-1 rounded-full bg-[#00D9C0]/10 text-[#00D9C0] font-bold text-sm mb-6 border border-[#00D9C0]/20" data-id="element-213">
              VALOR TOTAL: <span className="line-through opacity-60" data-id="element-214">$47</span>{' '}
              — HOY $0
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-215">
              Bonos <span className="text-[#00D9C0]" data-id="element-216">Gratis</span>
            </h2>
            <p className="text-xl text-zinc-400 leading-relaxed" data-id="element-217">
              Cuando entras a El Reset de 14 Días, no solo recibes el método.
              Recibes las herramientas para que de verdad lo ejecutes en tus
              días reales.
            </p>
          </FadeIn>
        </div>

        {/* 2. Bonus Cards */}
        <div className="space-y-24" data-id="element-218">
          {/* BONO #1 */}
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-219">
            <FadeIn className="order-1 md:order-1" data-id="element-220">
              <div className="relative group" data-id="element-221">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#00D9C0] to-teal-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" data-id="element-222"></div>
                <img src="https://cdn.magicpatterns.com/uploads/uvS2gTsKyn6yuJSsQP4FuU/imagen_bono_1.png" alt="Kit 10-Minutos: Tu Estación de Reset" className="relative w-full rounded-xl shadow-2xl shadow-[#00D9C0]/10 border border-zinc-800" data-id="element-223" />
              </div>
            </FadeIn>
            <FadeIn delay={0.2} className="order-2 md:order-2 space-y-6" data-id="element-224">
              <div className="flex items-center gap-3 mb-2" data-id="element-225">
                <span className="px-3 py-1 bg-[#00D9C0] text-black text-xs font-bold uppercase tracking-wider rounded-full" data-id="element-226">
                  Bono #1 Gratis
                </span>
                <span className="text-zinc-500 text-sm font-medium" data-id="element-227">
                  <span className="line-through mr-2" data-id="element-228">$17</span>{' '}
                  <span className="text-[#00D9C0]" data-id="element-229">$0</span>
                </span>
              </div>
              <h3 className="text-3xl font-bold text-white" data-id="element-230">
                Kit 10-Minutos: Tu Estación de Reset
              </h3>
              <p className="text-lg text-[#00D9C0] font-medium italic" data-id="element-231">
                "No te voy a pedir más disciplina. Te voy a dar
                infraestructura."
              </p>
              <p className="text-zinc-400" data-id="element-232">
                En 10 minutos vas a montar un "setup anti-excusa" para que el
                Micro-Reset sea casi automático.
              </p>
              <ul className="space-y-4 pt-2" data-id="element-233">
                <li className="flex items-start gap-3" data-id="element-234">
                  <ListChecks className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-235" />
                  <span className="text-zinc-300" data-id="element-236">
                    <strong data-id="element-237">Checklist "Setup Anti-Excusa"</strong> (para dejar
                    todo listo en 10 min)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-238">
                  <FileCheck className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-239" />
                  <span className="text-zinc-300" data-id="element-240">
                    <strong data-id="element-241">Plantilla "Acciones Mínimas"</strong> (para días
                    pesados)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-242">
                  <Smartphone className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-243" />
                  <span className="text-zinc-300" data-id="element-244">
                    <strong data-id="element-245">Recursos para celular</strong> (para que el reset
                    esté siempre a mano)
                  </span>
                </li>
              </ul>
              <p className="text-sm text-zinc-500 italic border-l-2 border-zinc-800 pl-4 mt-4" data-id="element-246">
                Traducción a vida real: cuando llegue el día difícil, no
                decides… ejecutas.
              </p>
            </FadeIn>
          </div>

          {/* BONO #2 */}
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-247">
            <FadeIn className="order-2 md:order-1 space-y-6" data-id="element-248">
              <div className="flex items-center gap-3 mb-2" data-id="element-249">
                <span className="px-3 py-1 bg-[#00D9C0] text-black text-xs font-bold uppercase tracking-wider rounded-full" data-id="element-250">
                  Bono #2 Gratis
                </span>
                <span className="text-zinc-500 text-sm font-medium" data-id="element-251">
                  <span className="line-through mr-2" data-id="element-252">$17</span>{' '}
                  <span className="text-[#00D9C0]" data-id="element-253">$0</span>
                </span>
              </div>
              <h3 className="text-3xl font-bold text-white" data-id="element-254">
                Mapa Anti-Caídas (7 Escenarios)
              </h3>
              <p className="text-lg text-[#00D9C0] font-medium italic" data-id="element-255">
                "Este es el bono que evita que un mal día se convierta en una
                mala semana."
              </p>
              <p className="text-zinc-400" data-id="element-256">
                El problema no es fallar. El problema es no saber qué hacer
                cuando estás ansioso, culpable o sin tiempo.
              </p>
              <ul className="space-y-4 pt-2" data-id="element-257">
                <li className="flex items-start gap-3" data-id="element-258">
                  <MapPin className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-259" />
                  <span className="text-zinc-300" data-id="element-260">
                    <strong data-id="element-261">7 escenarios reales</strong> (con pasos exactos, sin
                    improvisar)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-262">
                  <Target className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-263" />
                  <span className="text-zinc-300" data-id="element-264">
                    <strong data-id="element-265">Tarjetas imprimibles</strong> y versión móvil
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-266">
                  <ListChecks className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-267" />
                  <span className="text-zinc-300" data-id="element-268">
                    <strong data-id="element-269">Checklist "Retomar sin reiniciar"</strong>
                  </span>
                </li>
              </ul>
              <p className="text-sm text-zinc-500 italic border-l-2 border-zinc-800 pl-4 mt-4" data-id="element-270">
                Traducción a vida real: tu constancia ya no depende de cómo te
                sientes. Depende de que tengas un plan para cuando te sientes
                mal.
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="order-1 md:order-2" data-id="element-271">
              <div className="relative group" data-id="element-272">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#00D9C0] to-teal-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" data-id="element-273"></div>
                <img src="https://cdn.magicpatterns.com/uploads/ipNW7cxbE7rMqdS4geNar5/imagen_bono_2.png" alt="Mapa Anti-Caídas" className="relative w-full rounded-xl shadow-2xl shadow-[#00D9C0]/10 border border-zinc-800" data-id="element-274" />
              </div>
            </FadeIn>
          </div>

          {/* BONO #3 */}
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-275">
            <FadeIn className="order-1 md:order-1" data-id="element-276">
              <div className="relative group" data-id="element-277">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#00D9C0] to-teal-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" data-id="element-278"></div>
                <img src="https://cdn.magicpatterns.com/uploads/dZjaykyddAgfeNh8hYVA5u/imagen_bono_3.png" alt="Tracker de Evidencia" className="relative w-full rounded-xl shadow-2xl shadow-[#00D9C0]/10 border border-zinc-800" data-id="element-279" />
              </div>
            </FadeIn>
            <FadeIn delay={0.2} className="order-2 md:order-2 space-y-6" data-id="element-280">
              <div className="flex items-center gap-3 mb-2" data-id="element-281">
                <span className="px-3 py-1 bg-[#00D9C0] text-black text-xs font-bold uppercase tracking-wider rounded-full" data-id="element-282">
                  Bono #3 Gratis
                </span>
                <span className="text-zinc-500 text-sm font-medium" data-id="element-283">
                  <span className="line-through mr-2" data-id="element-284">$13</span>{' '}
                  <span className="text-[#00D9C0]" data-id="element-285">$0</span>
                </span>
              </div>
              <h3 className="text-3xl font-bold text-white" data-id="element-286">
                Tracker de Evidencia (14 Días)
              </h3>
              <p className="text-lg text-[#00D9C0] font-medium italic" data-id="element-287">
                "Si tu mente te castiga con 'no avanzas', este tracker te
                devuelve la verdad: sí estás volviendo."
              </p>
              <p className="text-zinc-400" data-id="element-288">
                Aquí no mides perfección. Mides evidencia.
              </p>
              <ul className="space-y-4 pt-2" data-id="element-289">
                <li className="flex items-start gap-3" data-id="element-290">
                  <BarChart3 className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-291" />
                  <span className="text-zinc-300" data-id="element-292">
                    <strong data-id="element-293">Tracker imprimible de 14 días</strong> (marca y
                    listo)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-294">
                  <Calendar className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-295" />
                  <span className="text-zinc-300" data-id="element-296">
                    <strong data-id="element-297">Tracker semanal</strong> (4 semanas)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-298">
                  <Trophy className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-299" />
                  <span className="text-zinc-300" data-id="element-300">
                    <strong data-id="element-301">Scoreboard de victorias</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-302">
                  <FileText className="w-5 h-5 text-[#00D9C0] shrink-0 mt-1" data-id="element-303" />
                  <span className="text-zinc-300" data-id="element-304">
                    <strong data-id="element-305">Plantilla de nota diaria</strong> (20 segundos)
                  </span>
                </li>
              </ul>
              <p className="text-sm text-zinc-500 italic border-l-2 border-zinc-800 pl-4 mt-4" data-id="element-306">
                Traducción a vida real: cada check es una prueba de que tú no
                vuelves a cero. Tú vuelves a ti.
              </p>
            </FadeIn>
          </div>
        </div>

        {/* 3. Bonus Summary Table */}
        <div className="max-w-2xl mx-auto mt-24" data-id="element-307">
          <FadeIn data-id="element-308">
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden" data-id="element-309">
              <div className="p-6 border-b border-zinc-800 bg-zinc-900/50" data-id="element-310">
                <h3 className="text-xl font-bold text-center" data-id="element-311">
                  Resumen de Bonos
                </h3>
              </div>
              <div className="divide-y divide-zinc-800" data-id="element-312">
                <div className="flex justify-between items-center p-4 md:p-6 hover:bg-zinc-800/30 transition-colors" data-id="element-313">
                  <div className="flex items-center gap-3" data-id="element-314">
                    <Gift className="w-5 h-5 text-[#00D9C0]" data-id="element-315" />
                    <span className="text-zinc-300 font-medium" data-id="element-316">
                      Kit 10-Minutos: Tu Estación de Reset
                    </span>
                  </div>
                  <span className="text-zinc-500 line-through" data-id="element-317">$17</span>
                </div>
                <div className="flex justify-between items-center p-4 md:p-6 hover:bg-zinc-800/30 transition-colors" data-id="element-318">
                  <div className="flex items-center gap-3" data-id="element-319">
                    <MapPin className="w-5 h-5 text-[#00D9C0]" data-id="element-320" />
                    <span className="text-zinc-300 font-medium" data-id="element-321">
                      Mapa Anti-Caídas (7 escenarios)
                    </span>
                  </div>
                  <span className="text-zinc-500 line-through" data-id="element-322">$17</span>
                </div>
                <div className="flex justify-between items-center p-4 md:p-6 hover:bg-zinc-800/30 transition-colors" data-id="element-323">
                  <div className="flex items-center gap-3" data-id="element-324">
                    <BarChart3 className="w-5 h-5 text-[#00D9C0]" data-id="element-325" />
                    <span className="text-zinc-300 font-medium" data-id="element-326">
                      Tracker de Evidencia (14 días)
                    </span>
                  </div>
                  <span className="text-zinc-500 line-through" data-id="element-327">$13</span>
                </div>
                <div className="flex justify-between items-center p-4 md:p-6 bg-zinc-950" data-id="element-328">
                  <span className="text-zinc-400 font-medium" data-id="element-329">Valor Total</span>
                  <span className="text-zinc-400 line-through" data-id="element-330">$47</span>
                </div>
                <div className="flex justify-between items-center p-4 md:p-6 bg-[#00D9C0]/5" data-id="element-331">
                  <span className="text-white font-bold text-lg" data-id="element-332">Hoy</span>
                  <span className="text-[#00D9C0] font-bold text-lg" data-id="element-333">
                    $0 (GRATIS)
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* 4. Transition + CTA */}
        <div className="max-w-3xl mx-auto text-center mt-20" data-id="element-334">
          <FadeIn data-id="element-335">
            <p className="text-2xl md:text-3xl font-bold text-white mb-6" data-id="element-336">
              No necesitas otro plan "perfecto".
            </p>
            <p className="text-xl text-zinc-400 mb-8" data-id="element-337">
              Necesitas un sistema que funcione cuando estás cansado/a, cuando
              estás ansioso/a y cuando tu mente negocia.
            </p>
            <p className="text-lg text-zinc-300 mb-10" data-id="element-338">
              Y por eso, además de El Reset de 14 Días, hoy te llevas estos 3
              bonos sin costo.
            </p>
            <Button href="https://eficienciadigital.ca/cart/48274359353594:1" onClick={handlePurchaseClick} className="w-full md:w-auto text-lg px-10 py-4" data-id="element-339">
              Descargar el Reset <ArrowRight className="ml-2 w-5 h-5" data-id="element-340" />
            </Button>
            <p className="text-sm text-zinc-500 mt-4" data-id="element-341">
              Empieza hoy. No el lunes.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Pricing Section */}
      <Section id="pricing" className="bg-gradient-to-b from-zinc-900 to-black border-t border-zinc-800" data-id="element-342">
        <div className="max-w-2xl mx-auto text-center" data-id="element-343">
          <FadeIn data-id="element-344">
            <div className="inline-block px-4 py-1 rounded-full bg-[#00D9C0]/10 text-[#00D9C0] font-bold text-sm mb-6" data-id="element-345">
              OFERTA DE LANZAMIENTO — 50% OFF
            </div>
            <div className="flex items-center justify-center gap-4 mb-2" data-id="element-346">
              <span className="text-3xl md:text-4xl text-zinc-500 line-through font-medium" data-id="element-347">
                $12.99
              </span>
            </div>
            <h2 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tighter" data-id="element-348">
              $6.39
            </h2>
            <p className="text-xl text-zinc-400 mb-8" data-id="element-349">
              Menos que una comida rápida para dos.
            </p>
            <p className="text-zinc-300 mb-10" data-id="element-350">
              Pero con un objetivo mucho más serio: que dejes de traicionarte
              cada semana.
            </p>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-351">
            <div className="bg-zinc-950 rounded-2xl border border-zinc-800 p-6 md:p-8 mb-10 text-left" data-id="element-352">
              <div className="flex items-center gap-2 mb-5" data-id="element-353">
                <Gift className="w-5 h-5 text-[#00D9C0]" data-id="element-354" />
                <h3 className="text-lg font-bold text-white" data-id="element-355">
                  Hoy incluye{' '}
                  <span className="text-[#00D9C0]" data-id="element-356">3 bonos gratis</span>
                  <span className="text-zinc-500 text-sm font-normal ml-2" data-id="element-357">
                    (valor <span className="line-through" data-id="element-358">$47</span>)
                  </span>
                </h3>
              </div>
              <div className="space-y-4" data-id="element-359">
                <div className="flex items-start gap-4 group" data-id="element-360">
                  <img src="https://cdn.magicpatterns.com/uploads/uvS2gTsKyn6yuJSsQP4FuU/imagen_bono_1.png" alt="Kit 10-Minutos" className="w-14 h-14 rounded-lg object-cover border border-zinc-800 shrink-0" data-id="element-361" />
                  <div className="flex-1 min-w-0" data-id="element-362">
                    <div className="flex items-center justify-between gap-2" data-id="element-363">
                      <span className="text-white font-semibold text-sm" data-id="element-364">
                        Kit 10-Minutos: Tu Estación de Reset
                      </span>
                      <span className="text-zinc-600 text-xs line-through shrink-0" data-id="element-365">
                        $17
                      </span>
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5" data-id="element-366">
                      Setup anti-excusa en 10 min
                    </p>
                  </div>
                </div>
                <div className="border-t border-zinc-800/50" data-id="element-367"></div>
                <div className="flex items-start gap-4 group" data-id="element-368">
                  <img src="https://cdn.magicpatterns.com/uploads/ipNW7cxbE7rMqdS4geNar5/imagen_bono_2.png" alt="Mapa Anti-Caídas" className="w-14 h-14 rounded-lg object-cover border border-zinc-800 shrink-0" data-id="element-369" />
                  <div className="flex-1 min-w-0" data-id="element-370">
                    <div className="flex items-center justify-between gap-2" data-id="element-371">
                      <span className="text-white font-semibold text-sm" data-id="element-372">
                        Mapa Anti-Caídas (7 Escenarios)
                      </span>
                      <span className="text-zinc-600 text-xs line-through shrink-0" data-id="element-373">
                        $17
                      </span>
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5" data-id="element-374">
                      Protocolo para cuando fallas
                    </p>
                  </div>
                </div>
                <div className="border-t border-zinc-800/50" data-id="element-375"></div>
                <div className="flex items-start gap-4 group" data-id="element-376">
                  <img src="https://cdn.magicpatterns.com/uploads/dZjaykyddAgfeNh8hYVA5u/imagen_bono_3.png" alt="Tracker de Evidencia" className="w-14 h-14 rounded-lg object-cover border border-zinc-800 shrink-0" data-id="element-377" />
                  <div className="flex-1 min-w-0" data-id="element-378">
                    <div className="flex items-center justify-between gap-2" data-id="element-379">
                      <span className="text-white font-semibold text-sm" data-id="element-380">
                        Tracker de Evidencia (14 Días)
                      </span>
                      <span className="text-zinc-600 text-xs line-through shrink-0" data-id="element-381">
                        $13
                      </span>
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5" data-id="element-382">
                      Mide evidencia, no perfección
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-zinc-800 flex items-center justify-between" data-id="element-383">
                <span className="text-zinc-400 text-sm" data-id="element-384">
                  Valor total de bonos
                </span>
                <div className="flex items-center gap-3" data-id="element-385">
                  <span className="text-zinc-600 text-sm line-through" data-id="element-386">
                    $47
                  </span>
                  <span className="text-[#00D9C0] font-bold" data-id="element-387">GRATIS</span>
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.25} data-id="element-388">
            <Button href="https://eficienciadigital.ca/cart/48274359353594:1" onClick={handlePurchaseClick} className="w-full md:w-auto text-xl px-12 py-5 shadow-[0_0_40px_rgba(0,217,192,0.3)] animate-pulse" data-id="element-389">
              Quiero el Reset ahora
            </Button>

            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-zinc-500" data-id="element-390">
              <span className="flex items-center gap-1" data-id="element-391">
                <Shield className="w-3 h-3" data-id="element-392" /> Pago seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-393">
                <Zap className="w-3 h-3" data-id="element-394" /> Entrega inmediata
              </span>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Closing Section */}
      <Section className="pb-32" data-id="element-395">
        <div className="max-w-2xl mx-auto text-center space-y-8" data-id="element-396">
          <FadeIn data-id="element-397">
            <p className="text-2xl md:text-3xl font-light text-white leading-relaxed" data-id="element-398">
              No necesitas convertirte en otra persona. <br data-id="element-399" />
              Necesitas dejar de pelearte contigo cuando estás ansioso/a.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-400">
            <p className="text-xl text-zinc-400" data-id="element-401">
              Hazlo fácil. Hazlo repetible. Hazlo hoy.
            </p>
          </FadeIn>

          <FadeIn delay={0.3} className="pt-8" data-id="element-402">
            <h3 className="text-4xl font-bold text-white mb-2" data-id="element-403">
              Elige 10 minutos.
            </h3>
            <p className="text-xl text-[#00D9C0]" data-id="element-404">
              Y gana la habilidad que te cambia la vida:{' '}
              <span className="font-bold underline decoration-2 underline-offset-4" data-id="element-405">
                volver
              </span>
              .
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-900" data-id="element-406">
        <div className="max-w-6xl mx-auto px-6" data-id="element-407">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-408">
            <p className="text-zinc-600 text-sm" data-id="element-409">
              © {new Date().getFullYear()} El Reset de 14 Días. Todos los
              derechos reservados.
            </p>
            <div className="flex items-center gap-6" data-id="element-410">
              <button onClick={() => setPrivacyOpen(true)} className="text-zinc-500 hover:text-[#00D9C0] text-sm transition-colors" data-id="element-411">
                Política de Privacidad
              </button>
              <span className="text-zinc-800" data-id="element-412">|</span>
              <button onClick={() => setTermsOpen(true)} className="text-zinc-500 hover:text-[#00D9C0] text-sm transition-colors" data-id="element-413">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}